import {
    TblCollectionDossierJsonldPanierunitaireRead,
    TblDossier2JsonldPanierunitaireRead,
    TblFacture2JsonldPanierunitaireRead,
    TblPanierReglementJsonldPanierunitaireRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { Card, Divider, Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import { tarifFrance } from '@europrocurement/l2d-utils';
import PanierReglementRecapElement from '../PanierReglementRecapElement/PanierReglementRecapElement';
import PanierReglementSelectedElement from '../PanierReglementSelectedElement/PanierReglementSelectedElement';
import PanierPay from '../PanierPay/PanierPay';
import PanierReglementTitle from '../PanierReglementTitle/PanierReglementTitle';
import { getNbElementSelected, sumttcAPayer } from './functions';
import PanierCardInfo from '../PanierCardInfo/PanierCardInfo';

export type PanierReglementProps = {
    panier: TblPanierReglementJsonldPanierunitaireRead;
    elementsSelectionnables?: boolean;
    type?: 'recouvrement' | 'paiement';
    onClick: (
        id: number,
        collectionsSelected: Array<TblCollectionDossierJsonldPanierunitaireRead>,
        dossiersSelected: Array<TblDossier2JsonldPanierunitaireRead>,
        facturesSelected: Array<TblFacture2JsonldPanierunitaireRead>,
    ) => void;
};

const PanierReglement: React.FunctionComponent<PanierReglementProps> = function ({
    panier,
    elementsSelectionnables = false,
    type = 'paiement',
    onClick,
}) {
    const currentDate: string = new Date().toLocaleDateString();

    const [collectionsSelected, setCollectionsSelected] = useState<
        Array<TblCollectionDossierJsonldPanierunitaireRead> | undefined
    >(panier.collections?.filter((c) => Number(c.acompteAttenduRestant ?? 0) > 0));
    const [facturesSelected, setFacturesSelected] = useState<
        Array<TblFacture2JsonldPanierunitaireRead> | undefined
    >(panier.factures?.filter((f) => Number(f.ttcRestant ?? 0) > 0));
    const [dossiersSelected, setDossiersSelected] = useState<
        Array<TblDossier2JsonldPanierunitaireRead> | undefined
    >(panier.dossiers?.filter((d) => Number(d.ttcRestant ?? 0) > 0));

    const nbElement = (panierReglement: TblPanierReglementJsonldPanierunitaireRead): number =>
        (panierReglement?.dossiers?.length ?? 0) +
        (panierReglement?.factures?.length ?? 0) +
        (panierReglement?.collections?.length ?? 0);

    const selectionnables: boolean = nbElement(panier) === 1 ? false : elementsSelectionnables;

    const factureSubtitle = (facture: TblFacture2JsonldPanierunitaireRead): string | undefined => {
        if (facture.dossierAl) {
            return `Facture d'annonce légale, dossier n°${facture.dossierAl.numero}`;
        }
        if (facture.collections) {
            const nums =
                facture.collections
                    ?.map((c) => c.numero)
                    .filter((n) => n !== undefined && n !== null)
                    .map(String) ?? [];

            if (nums.length === 0) return undefined;
            if (nums.length === 1) return `Facture de formalité, commande n°${nums[0]}`;
            return `Facture de formalité, commandes n°${nums.slice(0, -1).join(', ')} et ${nums[nums.length - 1]}`;
        }

        return undefined;
    };

    const ttcAPayer = sumttcAPayer(collectionsSelected, facturesSelected, dossiersSelected);

    const nbElementSelected = getNbElementSelected(
        collectionsSelected,
        facturesSelected,
        dossiersSelected,
    );

    return (
        <>
            <Grid container>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={7}
                >
                    <Card
                        sx={{
                            justifyContent: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}
                    >
                        <PanierReglementTitle
                            panier={panier}
                            selectionnables={selectionnables}
                        />
                        {panier?.dossiers
                            ? panier.dossiers
                                  .filter((d) => d.ttcRestant > 0)
                                  .map((dossier: TblDossier2JsonldPanierunitaireRead) => (
                                      <PanierReglementSelectedElement
                                          type={type}
                                          title={`Dossier n°${dossier.numero}`}
                                          subtitle={
                                              dossier.prestations && dossier.prestations.length > 0
                                                  ? `Annonce n°${dossier.prestations[0].numero}`
                                                  : undefined
                                          }
                                          onChange={(isChecked) => {
                                              setDossiersSelected((prev = []) => {
                                                  if (isChecked) {
                                                      if (!prev.includes(dossier)) {
                                                          return [...prev, dossier];
                                                      }
                                                      return prev;
                                                  }
                                                  return prev.filter((c) => c !== dossier);
                                              });
                                          }}
                                          checked={
                                              dossiersSelected?.some(
                                                  (d: TblDossier2JsonldPanierunitaireRead) =>
                                                      d.id === dossier.id,
                                              ) ?? false
                                          }
                                          disabled={dossier.ttcRestant <= 0}
                                          selectionnables={selectionnables}
                                          ttcRestant={dossier.ttcRestant}
                                      />
                                  ))
                            : null}
                        {panier?.collections
                            ? panier.collections
                                  .filter((c) => c.acompteAttenduRestant > 0)
                                  .map(
                                      (
                                          collection: TblCollectionDossierJsonldPanierunitaireRead,
                                      ) => (
                                          <PanierReglementSelectedElement
                                              type={type}
                                              title={`Commande n°${collection.numero}`}
                                              subtitle={(() => {
                                                  const nums =
                                                      collection.dossiers
                                                          ?.map((d) => d.numero)
                                                          .filter(
                                                              (n) => n !== undefined && n !== null,
                                                          )
                                                          .map(String) ?? [];

                                                  if (nums.length === 0) return undefined;
                                                  if (nums.length === 1)
                                                      return `Dossier n°${nums[0]}`;
                                                  return `Dossiers n°${nums.slice(0, -1).join(', ')} et ${nums[nums.length - 1]}`;
                                              })()}
                                              onChange={(isChecked) => {
                                                  setCollectionsSelected((prev = []) => {
                                                      if (isChecked) {
                                                          if (!prev.includes(collection)) {
                                                              console.log([...prev, collection]);
                                                              return [...prev, collection];
                                                          }
                                                          console.log(prev);
                                                          return prev;
                                                      }
                                                      console.log(
                                                          prev.filter((c) => c !== collection),
                                                      );
                                                      return prev.filter((c) => c !== collection);
                                                  });
                                              }}
                                              checked={
                                                  collectionsSelected?.some(
                                                      (
                                                          c: TblCollectionDossierJsonldPanierunitaireRead,
                                                      ) => c.id === collection.id,
                                                  ) ?? false
                                              }
                                              disabled={collection.acompteAttenduRestant <= 0}
                                              selectionnables={selectionnables}
                                              ttcRestant={collection.acompteAttenduRestant}
                                          />
                                      ),
                                  )
                            : null}
                        {panier?.factures
                            ? panier.factures
                                  .filter((f) => f.ttcRestant > 0)
                                  .map((facture: TblFacture2JsonldPanierunitaireRead) => (
                                      <PanierReglementSelectedElement
                                          type={type}
                                          title={`Facture n°${facture.numero}`}
                                          subtitle={factureSubtitle(facture)}
                                          onChange={(isChecked) => {
                                              setFacturesSelected((prev = []) => {
                                                  if (isChecked) {
                                                      if (!prev.includes(facture)) {
                                                          return [...prev, facture];
                                                      }
                                                      return prev;
                                                  }
                                                  return prev.filter((c) => c !== facture);
                                              });
                                          }}
                                          checked={
                                              facturesSelected?.some(
                                                  (f: TblFacture2JsonldPanierunitaireRead) =>
                                                      f.id === facture.id,
                                              ) ?? false
                                          }
                                          disabled={facture.ttcRestant <= 0}
                                          selectionnables={selectionnables}
                                          dateEcheance={
                                              facture.dateEcheanceFacture
                                                  ? facture.dateEcheanceFacture
                                                  : currentDate
                                          }
                                          dateCreation={facture.dateFacture}
                                          ttcRestant={facture.ttcRestant}
                                      />
                                  ))
                            : null}
                    </Card>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={5}
                >
                    <Card>
                        <PanierPay
                            type={type}
                            onClick={() => {
                                onClick(
                                    panier.id as number,
                                    collectionsSelected || [],
                                    dossiersSelected || [],
                                    facturesSelected || [],
                                );
                            }}
                            recouvrement={panier.chargeRecouvrement ?? undefined}
                            ttcAPayer={ttcAPayer}
                        />
                    </Card>
                    {selectionnables ? (
                        <Card
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                            }}
                        >
                            <Typography variant="l2dh6">Récapitulatif</Typography>
                            <Typography
                                variant="l2dlabel"
                                sx={{ color: 'text.gris70' }}
                            >
                                {nbElementSelected} élement(s) sélectionné(s)
                            </Typography>
                            <Divider />
                            <Box>
                                {dossiersSelected &&
                                    dossiersSelected.map(
                                        (dossier: TblDossier2JsonldPanierunitaireRead) => (
                                            <PanierReglementRecapElement
                                                title={`Dossier n°${dossier.numero}`}
                                                subtitle={
                                                    dossier.prestations &&
                                                    dossier.prestations.length > 0
                                                        ? `Annonce n°${dossier.prestations[0].numero}`
                                                        : undefined
                                                }
                                                prix={dossier.ttcRestant}
                                            />
                                        ),
                                    )}
                                {collectionsSelected &&
                                    collectionsSelected.map(
                                        (
                                            collection: TblCollectionDossierJsonldPanierunitaireRead,
                                        ) => (
                                            <PanierReglementRecapElement
                                                title={`Commande n°${collection.numero}`}
                                                subtitle={(() => {
                                                    const nums =
                                                        collection.dossiers
                                                            ?.map((d) => d.numero)
                                                            .filter(
                                                                (n) =>
                                                                    n !== undefined && n !== null,
                                                            )
                                                            .map(String) ?? [];

                                                    if (nums.length === 0) return undefined;
                                                    if (nums.length === 1)
                                                        return `Dossier n°${nums[0]}`;
                                                    return `Dossiers n°${nums.slice(0, -1).join(', ')} et ${nums[nums.length - 1]}`;
                                                })()}
                                                prix={collection.acompteAttenduRestant}
                                            />
                                        ),
                                    )}
                                {facturesSelected &&
                                    facturesSelected.map(
                                        (facture: TblFacture2JsonldPanierunitaireRead) => (
                                            <PanierReglementRecapElement
                                                title={`Facture n°${facture.numero}`}
                                                subtitle={factureSubtitle(facture)}
                                                prix={facture.ttcRestant}
                                            />
                                        ),
                                    )}
                            </Box>
                            <Divider />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    variant="l2dh5"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Total
                                </Typography>
                                <Typography
                                    variant="l2dh5"
                                    sx={{ fontWeight: 600 }}
                                >{`${tarifFrance(Math.round(ttcAPayer * 100) / 100)}`}</Typography>
                            </Box>
                        </Card>
                    ) : null}
                </Grid>
            </Grid>
            <PanierCardInfo selectionnables={selectionnables} />
        </>
    );
};

export default PanierReglement;
